import { Component, Input, SimpleChanges, OnChanges } from '@angular/core'
import { EDGE_TYPE, getEdgeDescription, Linqset, LinqsetEdge, NODE_TYPE, getConnectedModels } from '../../common/linqset-model';
import { NodeService } from '../../model-services/node.service';
import { IEdge, IGraph, INode } from '@yfiles/yfiles';
import { GraphProcessingService } from '../../model-services/graph-processing.service';
import { CHANGE_TYPE } from '../../common/linqset-change-model';
import { LinqsetRepositoryService } from '../../model-services/linqset-repository.service';

// one end of the connection: a node, or a model shown as a node
interface EdgeEnd {
    name:string
    type:NODE_TYPE
    kind:string
}

@Component({
    selector: 'app-edge-properties',
    templateUrl: './edge-properties.html',
    styleUrls: ['edge-properties.css'],
    standalone: false
})
export class EdgePropertiesComponent implements OnChanges {
    @Input() linqset:Linqset
    @Input() edge: IEdge
    @Input() graph: IGraph

    linqsetEdge:LinqsetEdge
    source:EdgeEnd
    target:EdgeEnd

    EDGE_TYPE = EDGE_TYPE

    // joins between models can be switched between passing items on and not
    canSwitch = false
    switchMessage = ""

    constructor(private nodeService:NodeService, private graphProcessing:GraphProcessingService, private repoService:LinqsetRepositoryService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['edge'] || changes['linqset']) this.setup()
    }

    private setup() {
        this.linqsetEdge = this.edge?.tag?.edge
        this.source = this.edge && this.describeEnd(this.edge.sourceNode)
        this.target = this.edge && this.describeEnd(this.edge.targetNode)
        this.checkCanSwitch()
    }

    private describeEnd(node:INode):EdgeEnd {
        const linqsetNode = node?.tag?.node
        if (linqsetNode) {
            const info = this.nodeService.getType(linqsetNode)
            return { name: linqsetNode.name || info?.name || 'Unnamed', type: linqsetNode.type, kind: info?.name || '' }
        }
        const model = node?.tag?.model
        if (model) return { name: model.name, type: NODE_TYPE.MODEL, kind: 'Model' }
        return undefined
    }

    getEdgeTypeDescription() {
        return this.linqsetEdge ? getEdgeDescription(this.linqsetEdge.type) : ''
    }

    isJoin() {
        return this.linqsetEdge?.type === EDGE_TYPE.JOINS || this.linqsetEdge?.type === EDGE_TYPE.USED_IN
    }

    // worked out when the edge changes, not while the page is drawing, as it can set a message
    private checkCanSwitch() {
        this.canSwitch = false
        this.switchMessage = ""
        if (!this.linqsetEdge || !this.isJoin()) return
        if (this.linqset?.readonly) {
            this.switchMessage = "This model is read-only, so the connection can't be changed."
            return
        }
        if (this.linqsetEdge.type === EDGE_TYPE.JOINS) {
            this.canSwitch = true
            return
        }
        // passing items on from a model that this model already feeds would make a loop
        const connectedModelKeys = this.linqset ? getConnectedModels(this.linqset.model, this.linqset.organisation.models, this.repoService.organisationLinqset).map(m => m._key) : []
        if (connectedModelKeys.indexOf(this.edge.sourceNode.tag?.node?.modelKey) < 0) this.canSwitch = true
        else this.switchMessage = "Items processed can't be passed on here, because the models are already connected the other way and it would create a loop."
    }

    toggleType() {
        this.linqsetEdge.type = this.linqsetEdge.type === EDGE_TYPE.JOINS ? EDGE_TYPE.USED_IN : EDGE_TYPE.JOINS
        this.graphProcessing.createLinqsetEdgesChangeEvent([this.linqsetEdge], CHANGE_TYPE.CHANGED, false, {_key: this.linqsetEdge._key, type: this.linqsetEdge.type})
    }
}
